<template>
  <div class="order-page">
    <header class="hero">
      <h1>Оформлення замовлення</h1>
      <p>Телефон, доставка та зручний канал зв'язку. Менеджер відповість протягом 30 хвилин.</p>
    </header>

    <div class="steps">
      <div v-for="(step, index) in steps" :key="step.label" class="step" :class="{ active: currentStep === index, done: currentStep > index }">
        <div class="dot">{{ currentStep > index ? '✓' : index + 1 }}</div>
        <div>
          <strong>{{ step.label }}</strong>
          <span>{{ step.sub }}</span>
        </div>
      </div>
    </div>

    <section class="card" v-if="currentStep === 0">
      <div class="section-head"><h2>Послуги</h2><p>Оберіть одну або кілька</p></div>
      <div v-if="loadingServices" class="loading">Завантажуємо послуги...</div>
      <div v-else class="services">
        <button v-for="service in displayServices" :key="service.key" type="button" class="service" :class="{ selected: selectedKeys.includes(service.key) }" @click="toggleKey(service.key)">
          <div class="check">{{ selectedKeys.includes(service.key) ? '✓' : '' }}</div>
          <div class="grow">
            <div class="name">{{ service.name }}</div>
            <div class="desc">{{ service.desc }}</div>
          </div>
          <div class="price">{{ service.price }}</div>
        </button>
      </div>
      <footer class="footer">
        <div>{{ selectedKeys.length ? `Обрано ${selectedKeys.length}, сума ${totalPriceLabel}` : 'Оберіть хоча б одну послугу' }}</div>
        <button class="primary" :disabled="!selectedKeys.length" @click="goNext">Далі</button>
      </footer>
    </section>

    <section class="card" v-if="currentStep === 1">
      <div class="section-head"><h2>Пристрій</h2><p>IMEI прибрано, залишили тільки потрібні поля</p></div>
      <div class="grid">
        <label>Бренд
          <select v-model="device.brand" class="field">
            <option value="">Оберіть бренд</option>
            <option value="Apple iPhone">Apple iPhone</option>
            <option value="Samsung Galaxy">Samsung Galaxy</option>
            <option value="Xiaomi">Xiaomi</option>
            <option value="Huawei">Huawei</option>
            <option value="OnePlus">OnePlus</option>
            <option value="Google Pixel">Google Pixel</option>
            <option value="Інший">Інший</option>
          </select>
        </label>
        <label>Модель
          <input v-model="device.model" class="field" type="text" placeholder="Наприклад: iPhone 14 Pro" />
        </label>
      </div>
      <label>Опишіть проблему
        <textarea v-model="device.issue" class="field area" rows="4" placeholder="Що сталося з телефоном?"></textarea>
      </label>
      <footer class="footer">
        <button class="ghost" @click="goPrev">Назад</button>
        <button class="primary" :disabled="!canProceedDeviceStep" @click="goNext">Далі</button>
      </footer>
    </section>

    <section class="card" v-if="currentStep === 2">
      <div class="section-head"><h2>Контакти та доставка</h2><p>Тільки номер телефону, без email</p></div>
      <div class="grid">
        <label>Ім'я клієнта
          <input v-model="contact.name" class="field" type="text" placeholder="Ваше ім'я" />
        </label>
        <label>Номер телефону
          <input v-model="contact.phone" class="field" type="tel" placeholder="+380..." />
        </label>
      </div>

      <div class="subcard">
        <h3>Доставка</h3>
        <div class="options">
          <label v-for="option in deliveryOptions" :key="option.value" class="option" :class="{ selected: delivery.method === option.value }">
            <input v-model="delivery.method" type="radio" :value="option.value" />
            <span>{{ option.label }}</span>
          </label>
        </div>
        <p v-if="delivery.method !== 'pickup'" class="hint">Нова пошта оформляється за рахунок покупця.</p>
      </div>

      <div v-if="delivery.method !== 'pickup'" class="subcard">
        <div class="grid">
          <label class="full">Повне ПІБ клієнта
            <input v-model="delivery.senderFullName" class="field" type="text" placeholder="Прізвище Ім'я По батькові" />
          </label>

          <label class="full auto">Місто
            <input v-model="delivery.city" class="field" type="text" placeholder="Почніть вводити місто" @input="onCityInput" @focus="showCityDropdown = cityOptions.length > 0" />
            <small class="hint">{{ shippingApiAvailable ? 'Автопошук Нової пошти увімкнений.' : 'API Нової пошти не налаштований, можна ввести вручну.' }}</small>
            <div v-if="showCityDropdown && cityOptions.length" class="dropdown">
              <button v-for="city in cityOptions" :key="city.ref" type="button" class="drop-item" @click="selectCity(city)">{{ city.label }}</button>
            </div>
          </label>
        </div>

        <label v-if="delivery.method === 'nova-poshta-branch'" class="auto">Відділення
          <input v-model="delivery.warehouse" class="field" type="text" placeholder="Оберіть відділення" @input="onWarehouseInput" @focus="showWarehouseDropdown = warehouseOptions.length > 0" />
          <div v-if="showWarehouseDropdown && warehouseOptions.length" class="dropdown">
            <button v-for="warehouse in warehouseOptions" :key="warehouse.ref" type="button" class="drop-item" @click="selectWarehouse(warehouse)">{{ warehouse.label }}</button>
          </div>
        </label>

        <div v-if="delivery.method === 'nova-poshta-courier'" class="grid">
          <label>Вулиця
            <input v-model="delivery.street" class="field" type="text" placeholder="Назва вулиці" />
          </label>
          <label>Будинок
            <input v-model="delivery.house" class="field" type="text" placeholder="№ будинку" />
          </label>
          <label class="full">Квартира
            <input v-model="delivery.apartment" class="field" type="text" placeholder="Необов'язково" />
          </label>
        </div>
      </div>

      <div class="subcard">
        <h3>Як краще зв'язатися</h3>
        <div class="options">
          <label v-for="channel in contactChannels" :key="channel.value" class="option" :class="{ selected: contact.preferredChannel === channel.value }">
            <input v-model="contact.preferredChannel" type="radio" :value="channel.value" />
            <span>{{ channel.label }}</span>
          </label>
        </div>
        <label v-if="contact.preferredChannel === 'telegram'">Telegram username або посилання
          <input v-model="contact.messengerHandle" class="field" type="text" placeholder="@username або https://t.me/..." />
        </label>
      </div>

      <label class="checkline"><input v-model="contact.callbackConsent" type="checkbox" /><span>Набрати клієнту або написати у Viber, WhatsApp чи Telegram, і менеджер зв'яжеться протягом півгодини.</span></label>
      <label class="checkline"><input v-model="agreeToTerms" type="checkbox" /><span>Погоджуюсь з умовами обслуговування та обробкою персональних даних.</span></label>

      <footer class="footer">
        <button class="ghost" @click="goPrev">Назад</button>
        <button class="primary" :disabled="!canProceedContactStep" @click="goNext">Далі</button>
      </footer>
    </section>

    <section class="card" v-if="currentStep === 3">
      <div v-if="!orderCreated">
        <div class="section-head"><h2>Перевірка</h2><p>Після відправлення ви отримаєте номер заявки від 1 до 40000</p></div>
        <div class="review">
          <div class="subcard">
            <h3>Послуги</h3>
            <div v-for="service in selectedServicesDisplay" :key="service.key" class="row"><span>{{ service.name }}</span><strong>{{ service.price }}</strong></div>
          </div>
          <div class="subcard">
            <h3>Пристрій</h3>
            <div class="row"><span>Бренд</span><strong>{{ device.brand }}</strong></div>
            <div class="row"><span>Модель</span><strong>{{ device.model }}</strong></div>
            <div class="stack"><span>Проблема</span><strong>{{ device.issue }}</strong></div>
          </div>
          <div class="subcard">
            <h3>Контакти</h3>
            <div class="row"><span>Ім'я</span><strong>{{ contact.name }}</strong></div>
            <div class="row"><span>Телефон</span><strong>{{ contact.phone }}</strong></div>
            <div class="row"><span>Канал</span><strong>{{ contactChannelLabel }}</strong></div>
            <div v-if="contact.messengerHandle" class="stack"><span>Telegram</span><strong>{{ contact.messengerHandle }}</strong></div>
          </div>
          <div class="subcard">
            <h3>Доставка</h3>
            <div class="row"><span>Спосіб</span><strong>{{ deliveryMethodLabel }}</strong></div>
            <div v-if="delivery.method !== 'pickup'" class="row"><span>ПІБ</span><strong>{{ delivery.senderFullName }}</strong></div>
            <div v-if="delivery.method !== 'pickup'" class="stack"><span>Адреса</span><strong>{{ deliverySummary }}</strong></div>
          </div>
        </div>
        <div class="total">{{ totalPriceLabel }}</div>
        <div v-if="error" class="error">{{ error }}</div>
        <footer class="footer">
          <button class="ghost" @click="goPrev">Назад</button>
          <button class="primary" :disabled="loading" @click="submitOrder">{{ loading ? 'Відправляємо...' : 'Оформити замовлення' }}</button>
        </footer>
      </div>

      <div v-else class="success">
        <div class="badge">🎉</div>
        <h2>Заявку створено</h2>
        <p>Номер вашої заявки: <strong>{{ newOrderNumber }}</strong></p>
        <p>Менеджер зв'яжеться з вами протягом 30 хвилин {{ contact.preferredChannel === 'call' ? 'за телефоном' : `через ${contactChannelLabel.toLowerCase()}` }}.</p>
        <div class="footer success-actions">
          <router-link class="ghost link-btn" to="/">На головну</router-link>
          <router-link v-if="isAuthenticated" class="primary link-btn" to="/orders">Мої замовлення</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { useAuthStore } from '../stores/auth';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const FALLBACK = [
  { key: 'screen', name: 'Заміна екрана', desc: 'Тріщини, дисплей або сенсор', price: 'від 800 грн', numPrice: 800 },
  { key: 'battery', name: 'Заміна батареї', desc: 'Швидко розряджається', price: 'від 400 грн', numPrice: 400 },
  { key: 'camera', name: 'Ремонт камери', desc: 'Камера не фокусується', price: 'від 600 грн', numPrice: 600 },
  { key: 'water', name: 'Ремонт після води', desc: 'Телефон потрапив у воду', price: 'від 500 грн', numPrice: 500 },
  { key: 'charge', name: "Ремонт роз'єму зарядки", desc: 'Не заряджається', price: 'від 350 грн', numPrice: 350 }
];
const deliveryOptions = [
  { value: 'pickup', label: 'Самовивіз' },
  { value: 'nova-poshta-branch', label: 'Нова пошта: відділення' },
  { value: 'nova-poshta-courier', label: "Нова пошта: кур'єр" }
];
const contactChannels = [
  { value: 'call', label: 'Подзвонити' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'viber', label: 'Viber' },
  { value: 'whatsapp', label: 'WhatsApp' }
];

export default {
  name: 'CreateOrderPage',
  setup() {
    const authStore = useAuthStore();
    const currentStep = ref(0);
    const loadingServices = ref(false);
    const loading = ref(false);
    const error = ref('');
    const orderCreated = ref(false);
    const newOrderNumber = ref('');
    const agreeToTerms = ref(false);
    const apiServices = ref([]);
    const selectedKeys = ref([]);
    const shippingApiAvailable = ref(true);
    const cityOptions = ref([]);
    const warehouseOptions = ref([]);
    const showCityDropdown = ref(false);
    const showWarehouseDropdown = ref(false);
    let cityDebounce = null;
    let warehouseDebounce = null;

    const device = ref({ brand: '', model: '', issue: '' });
    const contact = ref({ name: authStore.user?.name || '', phone: authStore.user?.phone || '', preferredChannel: 'call', messengerHandle: '', callbackConsent: false });
    const delivery = ref({ method: 'pickup', senderFullName: authStore.user?.name || '', city: '', cityRef: '', warehouse: '', warehouseRef: '', street: '', house: '', apartment: '' });
    const steps = [
      { label: 'Послуги', sub: 'Що ремонтуємо' },
      { label: 'Пристрій', sub: 'Марка та проблема' },
      { label: 'Контакти', sub: "Телефон і доставка" },
      { label: 'Перевірка', sub: 'Підтвердження' }
    ];

    const displayServices = computed(() => apiServices.value.length ? apiServices.value.map((service) => ({ key: service._id, name: service.name, desc: service.description || '', price: `${service.price} грн`, numPrice: Number(service.price) || 0 })) : FALLBACK);
    const selectedServicesDisplay = computed(() => displayServices.value.filter((service) => selectedKeys.value.includes(service.key)));
    const numericTotal = computed(() => selectedServicesDisplay.value.reduce((sum, service) => sum + (service.numPrice || 0), 0));
    const totalPriceLabel = computed(() => numericTotal.value ? `${numericTotal.value} грн` : 'За домовленістю');
    const isAuthenticated = computed(() => Boolean(authStore.token));
    const contactChannelLabel = computed(() => contactChannels.find((item) => item.value === contact.value.preferredChannel)?.label || 'Подзвонити');
    const deliveryMethodLabel = computed(() => deliveryOptions.find((item) => item.value === delivery.value.method)?.label || 'Самовивіз');
    const deliverySummary = computed(() => delivery.value.method === 'nova-poshta-branch' ? [delivery.value.city, delivery.value.warehouse].filter(Boolean).join(', ') : [delivery.value.city, delivery.value.street, delivery.value.house, delivery.value.apartment].filter(Boolean).join(', '));
    const canProceedDeviceStep = computed(() => Boolean(device.value.brand && device.value.model.trim() && device.value.issue.trim()));
    const canProceedContactStep = computed(() => {
      if (!contact.value.name.trim() || !contact.value.phone.trim() || !contact.value.callbackConsent || !agreeToTerms.value) return false;
      if (delivery.value.method === 'pickup') return true;
      if (!delivery.value.senderFullName.trim() || !delivery.value.city.trim()) return false;
      if (delivery.value.method === 'nova-poshta-branch') return Boolean(delivery.value.warehouse.trim());
      return Boolean(delivery.value.street.trim() && delivery.value.house.trim());
    });

    function toggleKey(key) {
      const index = selectedKeys.value.indexOf(key);
      index >= 0 ? selectedKeys.value.splice(index, 1) : selectedKeys.value.push(key);
    }
    function goNext() { if (currentStep.value < 3) currentStep.value += 1; }
    function goPrev() { if (currentStep.value > 0) currentStep.value -= 1; }

    async function loadServices() {
      loadingServices.value = true;
      try { apiServices.value = (await axios.get(`${API_URL}/services`)).data; } catch { apiServices.value = []; }
      finally { loadingServices.value = false; }
    }

    async function loadCities(query) {
      if (query.trim().length < 2) { cityOptions.value = []; showCityDropdown.value = false; return; }
      try {
        const response = await axios.get(`${API_URL}/shipping/nova-poshta/cities`, { params: { q: query } });
        shippingApiAvailable.value = true;
        cityOptions.value = response.data.items || [];
        showCityDropdown.value = cityOptions.value.length > 0;
      } catch (loadError) {
        if (loadError.response?.status === 503) shippingApiAvailable.value = false;
        cityOptions.value = [];
        showCityDropdown.value = false;
      }
    }

    async function loadWarehouses(query) {
      if (!delivery.value.cityRef) { warehouseOptions.value = []; showWarehouseDropdown.value = false; return; }
      try {
        const response = await axios.get(`${API_URL}/shipping/nova-poshta/warehouses`, { params: { cityRef: delivery.value.cityRef, q: query } });
        warehouseOptions.value = response.data.items || [];
        showWarehouseDropdown.value = warehouseOptions.value.length > 0;
      } catch {
        warehouseOptions.value = [];
        showWarehouseDropdown.value = false;
      }
    }

    function onCityInput() {
      delivery.value.cityRef = '';
      delivery.value.warehouse = '';
      delivery.value.warehouseRef = '';
      clearTimeout(cityDebounce);
      cityDebounce = setTimeout(() => loadCities(delivery.value.city), 300);
    }

    function onWarehouseInput() {
      delivery.value.warehouseRef = '';
      clearTimeout(warehouseDebounce);
      warehouseDebounce = setTimeout(() => loadWarehouses(delivery.value.warehouse), 300);
    }

    function selectCity(city) {
      delivery.value.city = city.city || city.label;
      delivery.value.cityRef = city.ref;
      showCityDropdown.value = false;
      loadWarehouses('');
    }

    function selectWarehouse(warehouse) {
      delivery.value.warehouse = warehouse.label;
      delivery.value.warehouseRef = warehouse.ref;
      showWarehouseDropdown.value = false;
    }

    async function submitOrder() {
      error.value = '';
      loading.value = true;
      try {
        const services = apiServices.value.length
          ? apiServices.value.filter((service) => selectedKeys.value.includes(service._id)).map((service) => ({ service: service._id, price: service.price }))
          : selectedServicesDisplay.value.map((service) => ({ serviceName: service.name, price: service.numPrice }));
        const headers = authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
        const payload = {
          services,
          deviceInfo: { brand: device.value.brand, model: device.value.model.trim(), issue: device.value.issue.trim() },
          totalPrice: numericTotal.value,
          contactInfo: { name: contact.value.name.trim(), phone: contact.value.phone.trim(), preferredChannel: contact.value.preferredChannel, messengerHandle: contact.value.messengerHandle.trim(), callbackConsent: contact.value.callbackConsent },
          deliveryInfo: { method: delivery.value.method, senderFullName: delivery.value.senderFullName.trim(), city: delivery.value.city.trim(), cityRef: delivery.value.cityRef, warehouse: delivery.value.warehouse.trim(), warehouseRef: delivery.value.warehouseRef, street: delivery.value.street.trim(), house: delivery.value.house.trim(), apartment: delivery.value.apartment.trim() }
        };
        const response = await axios.post(`${API_URL}/orders`, payload, { headers });
        newOrderNumber.value = response.data.order?.orderNumber || response.data.orderNumber || '';
        orderCreated.value = true;
      } catch (submitError) {
        error.value = submitError.response?.data?.error || 'Не вдалося створити заявку. Спробуйте ще раз.';
      } finally {
        loading.value = false;
      }
    }

    watch(() => delivery.value.method, (method) => {
      if (method === 'pickup') {
        delivery.value.city = '';
        delivery.value.cityRef = '';
        delivery.value.warehouse = '';
        delivery.value.warehouseRef = '';
        delivery.value.street = '';
        delivery.value.house = '';
        delivery.value.apartment = '';
      }
    });

    onMounted(loadServices);
    return { steps, currentStep, loadingServices, loading, error, orderCreated, newOrderNumber, agreeToTerms, selectedKeys, device, contact, delivery, deliveryOptions, contactChannels, displayServices, selectedServicesDisplay, totalPriceLabel, isAuthenticated, contactChannelLabel, deliveryMethodLabel, deliverySummary, canProceedDeviceStep, canProceedContactStep, shippingApiAvailable, cityOptions, warehouseOptions, showCityDropdown, showWarehouseDropdown, toggleKey, goNext, goPrev, onCityInput, onWarehouseInput, selectCity, selectWarehouse, submitOrder };
  }
};
</script>

<style scoped>
*{box-sizing:border-box}.order-page{max-width:980px;margin:0 auto;padding-bottom:2rem}.hero{text-align:center;margin-bottom:1rem}.hero h1{font-size:clamp(30px,5vw,42px);margin:0;color:#26215c}.hero p{margin:.5rem auto 0;max-width:700px;color:#666}.steps{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.75rem;margin-bottom:1rem}.step{display:flex;gap:.75rem;align-items:center;padding:.9rem 1rem;border:1px solid #e0dff8;border-radius:18px;background:#fff}.step .dot{width:34px;height:34px;border-radius:999px;background:#f1efff;color:#534ab7;display:flex;align-items:center;justify-content:center;font-weight:800;flex-shrink:0}.step.active,.step.done{border-color:#534ab7}.step.active .dot,.step.done .dot{background:#534ab7;color:#fff}.step strong{display:block;color:#26215c;font-size:14px}.step span{display:block;color:#7d7a9c;font-size:12px}.card{background:#fff;border:1px solid #e0dff8;border-radius:24px;padding:1.1rem;box-shadow:0 12px 28px rgba(83,74,183,.07)}.section-head{display:flex;justify-content:space-between;gap:1rem;align-items:flex-start;padding-bottom:.8rem;margin-bottom:1rem;border-bottom:1px solid #ecebfb}.section-head h2{margin:0;color:#26215c}.section-head p{margin:0;color:#666;font-size:14px}.loading{padding:2rem;text-align:center;color:#666}.services,.review{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.85rem}.service,.subcard{border:1px solid #e0dff8;border-radius:18px;background:#fbfbff;padding:1rem}.service{display:flex;gap:.75rem;align-items:flex-start;text-align:left;cursor:pointer}.service.selected{border-color:#534ab7;background:#f4f2ff}.check{width:24px;height:24px;border-radius:8px;background:#efedff;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;flex-shrink:0}.service.selected .check{background:#534ab7}.grow{flex:1}.name{font-weight:800;color:#26215c}.desc{font-size:14px;color:#666;margin-top:.25rem;line-height:1.4}.price{font-weight:800;color:#1d9e75;white-space:nowrap}.grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.85rem}.full{grid-column:1/-1}.field{width:100%;margin-top:.35rem;padding:.9rem 1rem;border-radius:14px;border:1.5px solid #e0dff8;background:#f8f7ff;color:#26215c;font:inherit}.area{min-height:110px;resize:vertical}.subcard h3{margin:0 0 .8rem;color:#26215c;font-size:16px}.options{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:.7rem}.option{display:flex;gap:.55rem;align-items:center;padding:.85rem .95rem;border:1.5px solid #e0dff8;border-radius:14px;background:#fff;cursor:pointer;font-size:14px}.option.selected{border-color:#534ab7;background:#f4f2ff}.option input,.checkline input{accent-color:#534ab7}.hint{display:block;margin-top:.45rem;color:#7d7a9c;font-size:12px;line-height:1.45}.auto{position:relative}.dropdown{position:absolute;left:0;right:0;top:100%;margin-top:4px;background:#fff;border:1px solid #d9d5fb;border-radius:16px;box-shadow:0 14px 28px rgba(31,29,74,.16);z-index:20;max-height:220px;overflow:auto}.drop-item{display:block;width:100%;padding:.8rem .95rem;background:#fff;border:none;border-bottom:1px solid #f1efff;text-align:left;cursor:pointer}.drop-item:last-child{border-bottom:none}.checkline{display:flex;gap:.7rem;align-items:flex-start;padding:.9rem 1rem;border-radius:16px;background:#f7f6ff;color:#4c496a;line-height:1.5;font-size:14px}.row,.stack{display:flex;justify-content:space-between;gap:.75rem;padding:.42rem 0;border-bottom:1px solid #ecebfb;color:#666;font-size:14px}.stack{flex-direction:column}.row:last-child,.stack:last-child{border-bottom:none}.row strong,.stack strong{color:#26215c}.total{margin-top:1rem;padding:1rem;border-radius:18px;background:linear-gradient(135deg,#eef2ff,#e6f8f0);text-align:center;font-size:34px;font-weight:900;color:#1d9e75}.error{margin-top:1rem;padding:.95rem 1rem;border-radius:16px;border:1px solid #f2c0c0;background:#fff0f0;color:#c0392b}.footer{display:flex;justify-content:space-between;align-items:center;gap:.75rem;margin-top:1rem;padding-top:1rem;border-top:1px solid #ecebfb}.primary,.ghost,.link-btn{padding:.92rem 1.2rem;border-radius:14px;font-weight:800;text-align:center;text-decoration:none}.primary{border:none;background:linear-gradient(135deg,#534ab7,#7b72dd);color:#fff;box-shadow:0 10px 24px rgba(83,74,183,.2)}.ghost,.link-btn.ghost{border:1.5px solid #d9d5fb;background:#fff;color:#534ab7}.primary:disabled{opacity:.55;box-shadow:none}.success{text-align:center;padding:1rem 0}.badge{font-size:44px}.success h2{margin:.7rem 0 0;color:#26215c}.success p{max-width:560px;margin:.8rem auto 0;color:#666;line-height:1.6}.success-actions{justify-content:center}@media (max-width:900px){.steps,.services,.review,.grid{grid-template-columns:1fr 1fr}.steps{grid-template-columns:repeat(2,minmax(0,1fr))}}@media (max-width:640px){.steps,.services,.review,.grid,.options{grid-template-columns:1fr}.card{padding:1rem;border-radius:20px}.section-head,.footer{flex-direction:column;align-items:stretch}.primary,.ghost,.link-btn{width:100%}.step{padding:.8rem .9rem}}
</style>
